import { Controller } from "@hotwired/stimulus"
import SlimSelect from "slim-select"
import { slimSelectSettings } from "../helpers/slim_select_helpers"

// Mirrors Billing::EmailAddressList.valid? on the server (URI::MailTo::EMAIL_REGEXP plus a
// required top-level domain). Anything looser is accepted here and silently dropped by the
// e-mail job — e.g. an umlaut domain, which would need punycode.
const LABEL = "[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?"
const EMAIL_PATTERN = new RegExp(`^[a-zA-Z0-9.!#$%&'*+/=?^_\`{|}~-]+@${LABEL}(?:\\.${LABEL})+$`)
// Same as Billing::EmailAddressList::SEPARATOR.
const SEPARATOR = /[,;\s]+/

// Recipient picker for the billing send dialog: a multi-select over the customer's address
// book (the server-rendered <option>s) that also takes typed or pasted addresses. Each
// selected address posts as one `name[]` value.
//
// A deliberate exception to "reuse select_controller": its addable path closes the dropdown
// and hands the term to a listener (quick-create dialogs), whereas this picker adds the term
// in place, keeps the dropdown open, reports an invalid term inline through SlimSelect's
// addable error and blocks the form submit. Everything else (search, chips) is plain SlimSelect.
export default class extends Controller {
  connect() {
    const { placeholder, searchPlaceholder, addableText, invalidText, requiredText } =
      this.element.dataset
    this.invalidText = invalidText
    this.requiredText = requiredText

    this.slimselect = new SlimSelect({
      select: this.element,
      settings: slimSelectSettings({
        placeholderText: placeholder,
        searchPlaceholder,
        searchText: searchPlaceholder,
        // SlimSelect hides its addable hint while allowDeselect injects a blank option;
        // each chip has its own remove button anyway.
        allowDeselect: false,
        closeOnSelect: false,
        ...(addableText ? { addableText } : {}),
      }),
      events: {
        addable: (term) => this.addTyped(term),
        afterOpen: () => (this.open = true),
        afterClose: () => (this.open = false),
      },
    })

    // Clicking an option moves focus out of SlimSelect, so its own Escape handling no longer
    // runs. Catch Escape first (capture phase) while the dropdown is open, close the dropdown
    // and cancel the event so the surrounding modal stays open.
    this.closeOnEscape = (event) => {
      if (event.key !== "Escape" || !this.open) return
      event.preventDefault()
      this.slimselect.close()
    }
    document.addEventListener("keydown", this.closeOnEscape, true)

    // The hidden <select> can't carry `required` (the browser can't focus it to report), and a
    // server-side rejection would redirect away from the dialog and lose the edited text. So
    // the picker blocks the submit itself and says why.
    this.form = this.element.form
    this.validateOnSubmit = (event) => this.validate(event)
    this.form?.addEventListener("submit", this.validateOnSubmit)
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeOnEscape, true)
    this.form?.removeEventListener("submit", this.validateOnSubmit)
    this.errorElement?.remove()
    this.slimselect?.destroy()
  }

  // A typed or pasted term may hold several addresses ("a@x.de; b@x.de"). Every valid one is
  // added (or, if the address book already has it, selected) here; SlimSelect is told not to
  // add anything itself. Nothing is added when any part is invalid, so the user can fix it.
  addTyped(term) {
    const addresses = term.split(SEPARATOR).filter(Boolean)
    if (addresses.length === 0 || !addresses.every((address) => EMAIL_PATTERN.test(address))) {
      return new Error(this.invalidText)
    }

    addresses.forEach((address) => this.select(address))
    this.slimselect.search("")
    return false
  }

  // Blocks the submit when this picker is required but empty, or when a selected address (an
  // invalid prefill) would be dropped by the server.
  validate(event) {
    const selected = this.slimselect.getSelected().filter(Boolean)
    const invalid = selected.filter((address) => !EMAIL_PATTERN.test(address))
    let message = null
    if (invalid.length > 0) message = `${this.invalidText}: ${invalid.join(", ")}`
    else if (this.element.dataset.required === "true" && selected.length === 0)
      message = this.requiredText

    this.showError(message)
    if (message) event.preventDefault()
  }

  showError(message) {
    if (!message) {
      this.errorElement?.remove()
      this.errorElement = null
      return
    }
    if (!this.errorElement) {
      this.errorElement = document.createElement("p")
      this.errorElement.className = "mt-1 text-xs text-red-600"
      this.errorElement.setAttribute("role", "alert")
      this.element.parentElement.appendChild(this.errorElement)
    }
    this.errorElement.textContent = message
  }

  select(address) {
    const existing = this.slimselect
      .getData()
      .find((option) => option.value?.toLowerCase() === address.toLowerCase())
    const value = existing ? existing.value : address
    if (!existing) this.slimselect.addOption({ text: address, value })

    const selected = this.slimselect.getSelected()
    if (!selected.includes(value)) this.slimselect.setSelected([...selected, value])
  }
}
